/**
 * Horizontal Allocator
 * 
 * Horizontal-first positioning system using absolute positioning.
 * - Full-height views: always at top (y = containerPadding), placed horizontally
 * - Half-height views: can stack vertically at same X position (two per column)
 * - Custom-height views: placed horizontally, can stack if small enough
 * Uses viewport dimensions to maximize horizontal space usage.
 */

import type { BaseViewHTML } from './base-view-html.js';
import { viewManager } from './view-manager.js';
import { log } from '../services/logging/index.js';
import { state, setState, updateState } from '../core/state.js';
import { bus } from '../core/events.js';
import { getViewGap, getContainerPadding, getStandardViewWidth, getAvailableHeight, isMobileViewport, getVerticalPadding } from './layout-constants.js';

// Track horizontal position for new views
const viewGap = getViewGap(); // Gap between views (modest margin, like old grid system)

function getDefaultViewWidth(): number {
  return getStandardViewWidth();
}

function getDefaultViewHeight(): number {
  return getAvailableHeight();
}

// Cached reference to .layer-content element to avoid repeated querySelector calls
let cachedLayerContentElement: HTMLElement | null = null;

// Visualization lines for horizontal allocator boundaries
let topBoundaryLine: HTMLElement | null = null;
let bottomBoundaryLine: HTMLElement | null = null;

/**
 * Set the cached reference to the layer content element
 * Called by view-manager when the content wrapper is created
 */
export function setLayerContentElement(element: HTMLElement | null): void {
  cachedLayerContentElement = element;
  // Visualization lines disabled for now
  // Remove any existing visualization lines
  if (element) {
    const existingLines = element.querySelectorAll('.horizontal-allocator-boundary-line');
    existingLines.forEach(line => line.remove());
    topBoundaryLine = null;
    bottomBoundaryLine = null;
  }
  // if (element) {
  //   updateVisualizationLines();
  // }
}

/**
 * Update visualization lines showing horizontal allocator boundaries
 * Shows top and bottom lines for full-height view area
 */
function updateVisualizationLines(): void {
  const contentWrapper = cachedLayerContentElement;
  if (!contentWrapper) return;
  
  // Get viewport dimensions
  const verticalPadding = getVerticalPadding();
  const viewportHeight = window.innerHeight;
  const availableHeight = viewportHeight - (verticalPadding * 2);
  const topY = verticalPadding;
  const bottomY = verticalPadding + availableHeight;
  
  // Get container width for full-width lines
  const containerWidth = contentWrapper.scrollWidth || window.innerWidth;
  
  // Create or update top boundary line
  if (!topBoundaryLine) {
    topBoundaryLine = document.createElement('div');
    topBoundaryLine.className = 'horizontal-allocator-boundary-line';
    topBoundaryLine.setAttribute('data-boundary', 'top');
    contentWrapper.appendChild(topBoundaryLine);
  }
  
  // Create or update bottom boundary line
  if (!bottomBoundaryLine) {
    bottomBoundaryLine = document.createElement('div');
    bottomBoundaryLine.className = 'horizontal-allocator-boundary-line';
    bottomBoundaryLine.setAttribute('data-boundary', 'bottom');
    contentWrapper.appendChild(bottomBoundaryLine);
  }
  
  // Update top line position and width
  topBoundaryLine.style.cssText = `
    position: absolute;
    left: 0;
    top: ${topY}px;
    width: ${containerWidth}px;
    height: 1px;
    background: rgba(102, 204, 255, 0.4);
    pointer-events: none;
    z-index: 1;
    box-shadow: 0 0 2px rgba(102, 204, 255, 0.3);
  `;
  
  // Update bottom line position and width
  bottomBoundaryLine.style.cssText = `
    position: absolute;
    left: 0;
    top: ${bottomY}px;
    width: ${containerWidth}px;
    height: 1px;
    background: rgba(102, 204, 255, 0.4);
    pointer-events: none;
    z-index: 1;
    box-shadow: 0 0 2px rgba(102, 204, 255, 0.3);
  `;
}

// Visualization lines disabled for now
// Update visualization lines on window resize
// let resizeTimeout: number | null = null;
// window.addEventListener('resize', () => {
//   if (resizeTimeout) {
//     clearTimeout(resizeTimeout);
//   }
//   resizeTimeout = window.setTimeout(() => {
//     updateVisualizationLines();
//   }, 100); // Debounce resize events
// });

/**
 * Allocate a new view using horizontal-first positioning
 * 
 * New views are added to the beginning of the order, then all views are repositioned.
 * This ensures deterministic positioning without collisions.
 */
export function allocateViewHorizontally(view: BaseViewHTML): void {
  
  // CRITICAL: Ensure the new view is in the order FIRST (at index 0)
  // This must happen synchronously before allocation
  const currentOrder = state.viewOrder || [];
  const filteredOrder = currentOrder.filter(id => id !== view.id);
  const newOrder = [view.id, ...filteredOrder];
  
  if (!state.viewOrder) {
    setState('viewOrder', newOrder);
  } else {
    updateState('viewOrder', () => newOrder);
  }
  
  // Now allocate all views - the new view will be positioned first (leftmost)
  allocateAllViews();
  
}

/**
 * Get all views and ensure they're positioned in horizontal-first layout
 * 
 * Recalculates all positions based on viewport dimensions.
 * Maintains order (newest first = leftmost).
 * Full-height views at top, half-height views can stack.
 */
export function allocateAllViews(): void {
  const views = viewManager.getAllViews();
  
  // Get viewport dimensions
  const horizontalPadding = getContainerPadding();
  const verticalPadding = getVerticalPadding();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const availableWidth = viewportWidth - (horizontalPadding * 2);
  const availableHeight = viewportHeight - (verticalPadding * 2);
  
  
  // Get views in index-based order (from state.viewOrder)
  // If no order exists, use DOM order and save it
  let viewOrder = state.viewOrder || [];
  
  // CRITICAL: Clean up viewOrder to remove any stale IDs (views that no longer exist)
  // This prevents ordering issues when views are deleted
  const existingViewIds = new Set(views.keys());
  const cleanedOrder = viewOrder.filter(id => existingViewIds.has(id));
  
  // If order was cleaned, update state
  if (cleanedOrder.length !== viewOrder.length) {
    const previousOrder = state.viewOrder || [];
    if (!state.viewOrder) {
      setState('viewOrder', cleanedOrder);
    } else {
      updateState('viewOrder', () => cleanedOrder);
    }
    viewOrder = cleanedOrder;
    
    // Emit event if order changed (cleaned up stale views)
    const orderChanged = JSON.stringify(previousOrder) !== JSON.stringify(cleanedOrder);
    if (orderChanged) {
      bus.emit('view.reordered', {
        viewIds: cleanedOrder,
        previousViewIds: previousOrder,
        timestamp: Date.now()
      });
    }
  }
  
  const sortedViews: BaseViewHTML[] = [];
  
  // If we have a saved order, use it
  if (viewOrder.length > 0) {
    for (const viewId of viewOrder) {
      const view = views.get(viewId);
      if (view) {
        const vMetadata = view.getMetadata();
        {
          sortedViews.push(view);
        }
      }
    }
    // Add any views not in the order (newly created views)
    for (const view of views.values()) {
      if (!viewOrder.includes(view.id)) {
        const vMetadata = view.getMetadata();
        {
          sortedViews.push(view);
        }
      }
    }
  } else {
    // No saved order - use DOM order and save it
    const contentWrapper = cachedLayerContentElement || (document.querySelector('.layer-content') as HTMLElement);
    const domOrder: string[] = [];
  
  if (contentWrapper) {
    for (let i = 0; i < contentWrapper.children.length; i++) {
      const element = contentWrapper.children[i] as HTMLElement;
      const viewId = element.getAttribute('data-view-id');
      if (viewId) {
        const view = views.get(viewId);
        if (view) {
            const vMetadata = view.getMetadata();
            {
              sortedViews.push(view);
              domOrder.push(viewId);
            }
          }
        }
      }
    } else {
      // Fallback: use map order
      for (const view of views.values()) {
        const vMetadata = view.getMetadata();
        {
          sortedViews.push(view);
          domOrder.push(view.id);
        }
      }
    }
    
    // Save the order for future use
    if (domOrder.length > 0) {
      const previousOrder = state.viewOrder || [];
      const orderChanged = JSON.stringify(previousOrder) !== JSON.stringify(domOrder);
      
      if (!state.viewOrder) {
        setState('viewOrder', domOrder);
  } else {
        updateState('viewOrder', () => domOrder);
      }
      
      // Emit event if order changed
      if (orderChanged) {
        bus.emit('view.reordered', {
          viewIds: domOrder,
          previousViewIds: previousOrder,
          timestamp: Date.now()
        });
      }
    }
  }
  
  // CRITICAL: Process views in ORDER (not grouped by height mode)
  // This ensures deterministic positioning that respects the order array
  // Full-height views flow horizontally, half-height views can stack vertically using Tetris-style shelf tracking
  
  
  let currentX = horizontalPadding; // Always start at containerPadding (never negative)
  const fullHeightY = verticalPadding;
  const bottomShelfY = verticalPadding + (availableHeight / 2) + viewGap;
  // Track the actual right edge of each shelf (for accurate positioning)
  let topShelfRightEdge = currentX;    // Right edge of top shelf views
  let bottomShelfRightEdge = currentX;  // Right edge of bottom shelf views
  
  // Track the actual right edge of the current column (for accurate spacing)
  // When half-height views are placed, we track the maximum right edge (x + width)
  let currentColumnRightEdge = currentX; // Right edge of current column
  let currentColumnStartX = currentX; // Start of the current column
  
  
  // Process each view in order
  for (const view of sortedViews) {
    // Skip overlay views (fixed position, not managed by allocator)
    const metadata = view.getMetadata();
    if (metadata.isOverlay) {
      continue;
    }

    const geometry = view.getGeometry();
    const width = geometry.width || getDefaultViewWidth();
    const height = geometry.height || getDefaultViewHeight();
    const heightMode = metadata?.heightMode ?? 'full';
    
    const columnHasHalfViews = (topShelfRightEdge > currentColumnStartX) || (bottomShelfRightEdge > currentColumnStartX);
    
    if (heightMode === 'full') {
      // Full-height view: occupies entire column (both shelves)
      
      // If current column has half-height views, move past that column first
      if (columnHasHalfViews) {
        currentX = currentColumnRightEdge + viewGap;
        currentColumnStartX = currentX;
        topShelfRightEdge = currentX;
        bottomShelfRightEdge = currentX;
        currentColumnRightEdge = currentX;
      }
      
      view.updateGeometry({
        x: currentX,
        y: fullHeightY,
        width,
        height
      });
      
      // Calculate right edge BEFORE updating currentX
      const rightEdge = currentX + width;
      currentColumnRightEdge = rightEdge;
      currentX = rightEdge + viewGap;
      
      // Reset shelves for the next column
      currentColumnStartX = currentX;
      topShelfRightEdge = currentX;
      bottomShelfRightEdge = currentX;
      currentColumnRightEdge = currentX;
      
    } else if (heightMode === 'half') {
      // Half-height view: place on the shelf with the shorter occupied width
      const topHasViews = topShelfRightEdge > currentColumnStartX;
      const bottomHasViews = bottomShelfRightEdge > currentColumnStartX;
      
      const topX = topHasViews ? topShelfRightEdge + viewGap : topShelfRightEdge;
      const bottomX = bottomHasViews ? bottomShelfRightEdge + viewGap : bottomShelfRightEdge;
      
      const placeOnTop = topShelfRightEdge <= bottomShelfRightEdge;
      const x = placeOnTop ? topX : bottomX;
      const y = placeOnTop ? fullHeightY : bottomShelfY;
      
      view.updateGeometry({
        x,
        y,
        width,
        height
      });
      
      if (placeOnTop) {
        topShelfRightEdge = x + width;
      } else {
        bottomShelfRightEdge = x + width;
      }
      
      currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
    } else {
      // Custom-height views: treat as full-height (flow horizontally)
      if (columnHasHalfViews) {
        currentX = currentColumnRightEdge + viewGap;
        currentColumnStartX = currentX;
        topShelfRightEdge = currentX;
        bottomShelfRightEdge = currentX;
        currentColumnRightEdge = currentX;
      }
      
    view.updateGeometry({
      x: currentX,
        y: fullHeightY,
      width,
      height
    });
    
      // Calculate right edge BEFORE updating currentX
      const rightEdge = currentX + width;
      currentColumnRightEdge = rightEdge;
      currentX = rightEdge + viewGap;
      
      currentColumnStartX = currentX;
      topShelfRightEdge = currentX;
      bottomShelfRightEdge = currentX;
      currentColumnRightEdge = currentX;
      
    }
  }
  
  
  // Update container width to accommodate all views (for horizontal scrolling)
  updateContainerWidth(sortedViews);
  
  // Visualization lines disabled for now
  // updateVisualizationLines();
  
}

if (typeof window !== 'undefined') {
  let lastIsMobile = isMobileViewport();
  window.addEventListener('resize', () => {
    const nextIsMobile = isMobileViewport();
    if (nextIsMobile !== lastIsMobile) {
      lastIsMobile = nextIsMobile;
      allocateAllViews();
    }
  });
}

/**
 * Adjust views around a resized view without moving the resized view
 * This keeps the resized view in place and shifts other views to accommodate size changes
 */
export function adjustViewsAroundResizedView(resizedView: BaseViewHTML): void {
  const views = viewManager.getAllViews();
  const allViews = Array.from(views.values());
  
  // Get the resized view's current position and new size
  const horizontalPadding = getContainerPadding();
  const verticalPadding = getVerticalPadding();
  const resizedGeometry = resizedView.getGeometry();
  const resizedX = resizedGeometry.x || horizontalPadding;
  const resizedWidth = resizedGeometry.width || getDefaultViewWidth();
  const resizedId = resizedView.id;
  
  // Get viewport dimensions
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const availableHeight = viewportHeight - (verticalPadding * 2);
  
  // Get views in index-based order (from state.viewOrder)
  const viewOrder = state.viewOrder || [];
  const sortedViews: BaseViewHTML[] = [];
  
  // If we have a saved order, use it
  if (viewOrder.length > 0) {
    for (const viewId of viewOrder) {
      const view = views.get(viewId);
      if (view && view.id === resizedId) {
        // Skip the resized view - it stays in place
        continue;
      }
      if (view) {
        const vMetadata = view.getMetadata();
        {
          sortedViews.push(view);
        }
      }
    }
    // Add any views not in the order (newly created views)
    for (const view of views.values()) {
      if (!viewOrder.includes(view.id) && view.id !== resizedId) {
        const vMetadata = view.getMetadata();
        {
          sortedViews.push(view);
        }
      }
    }
  } else {
    // Fallback: use DOM order
    const contentWrapper = cachedLayerContentElement || (document.querySelector('.layer-content') as HTMLElement);
    if (contentWrapper) {
      for (let i = 0; i < contentWrapper.children.length; i++) {
        const element = contentWrapper.children[i] as HTMLElement;
        const viewId = element.getAttribute('data-view-id');
        if (viewId && viewId !== resizedId) {
          const view = views.get(viewId);
          if (view) {
            const vMetadata = view.getMetadata();
            {
              sortedViews.push(view);
            }
          }
        }
      }
    } else {
      for (const view of allViews) {
        if (view.id !== resizedId) {
          sortedViews.push(view);
        }
      }
    }
  }
  
  // Find the resized view's position in the order array
  const resizedIndex = viewOrder.indexOf(resizedId);
  if (resizedIndex === -1) {
    // Resized view not found, fall back to full reallocation
    allocateAllViews();
    return;
  }
  
  // Keep the resized view at its current X position (it already has the new size)
  // Views to the left of the resized view stay in place (don't move them)
  // Views to the right of the resized view shift based on the new size
  
  // Calculate the right edge of the resized view with its new width
  const resizedRightEdge = resizedX + resizedWidth;
  
  // Position views to the left of the resized view (they stay in place)
  // Use the same Tetris-style algorithm as allocateAllViews()
  let currentX = horizontalPadding; // Always start at containerPadding
  const fullHeightY = verticalPadding;
  const bottomShelfY = verticalPadding + (availableHeight / 2) + viewGap;
  // Tetris-style shelf tracking
  let topShelfOccupied = 0;
  let bottomShelfOccupied = 0;
  
  // Track the actual right edge of each shelf (for accurate positioning)
  let topShelfRightEdge = currentX;
  let bottomShelfRightEdge = currentX;
  
  // Track the actual right edge of the current column (for accurate spacing)
  let currentColumnRightEdge = currentX; // Right edge of current column
  
  for (let i = 0; i < resizedIndex; i++) {
    const viewId = viewOrder[i];
    const view = views.get(viewId);
    if (!view) continue;
    
    const geometry = view.getGeometry();
    const width = geometry.width || getDefaultViewWidth();
    const height = geometry.height || getDefaultViewHeight();
    const metadata = view.getMetadata();
    const heightMode = metadata?.heightMode ?? 'full';
    const widthMode = metadata?.widthMode ?? 'standard';
    
    if (heightMode === 'full') {
      // Full-height view: occupies entire column (both shelves)
      if (topShelfOccupied > 0 || bottomShelfOccupied > 0) {
        currentX = currentColumnRightEdge + viewGap;
      }
      
      view.updateGeometry({
        x: currentX,
        y: fullHeightY,
        width,
        height
      });
      
      // Calculate right edge BEFORE updating currentX
      currentColumnRightEdge = currentX + width;
      currentX = currentColumnRightEdge + viewGap;
      
      topShelfOccupied = 2;
      bottomShelfOccupied = 2;
      topShelfRightEdge = currentColumnRightEdge;
      bottomShelfRightEdge = currentColumnRightEdge;
    } else if (heightMode === 'half') {
      // Half-height view: Tetris-style placement
      const unitsNeeded = widthMode === 'double' ? 2 : 1;
      
      if (topShelfOccupied + unitsNeeded <= 2) {
        // Fits on top shelf - add gap if shelf already has views
        const x = topShelfOccupied > 0 ? topShelfRightEdge + viewGap : topShelfRightEdge;
        view.updateGeometry({
          x,
          y: fullHeightY,
          width,
          height
        });
        topShelfOccupied += unitsNeeded;
        topShelfRightEdge = x + width;
        currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
      } else if (bottomShelfOccupied + unitsNeeded <= 2) {
        // Fits on bottom shelf - add gap if shelf already has views
        const x = bottomShelfOccupied > 0 ? bottomShelfRightEdge + viewGap : bottomShelfRightEdge;
        view.updateGeometry({
          x,
          y: bottomShelfY,
          width,
          height
        });
        bottomShelfOccupied += unitsNeeded;
        bottomShelfRightEdge = x + width;
        currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
      } else {
        // Both shelves full - start new column
        currentX = currentColumnRightEdge + viewGap;
        topShelfOccupied = unitsNeeded;
        bottomShelfOccupied = 0;
        topShelfRightEdge = currentX;
        bottomShelfRightEdge = currentX;
        currentColumnRightEdge = currentX;
        view.updateGeometry({
          x: currentX,
          y: fullHeightY,
          width,
          height
        });
        topShelfRightEdge = currentX + width;
        currentColumnRightEdge = topShelfRightEdge;
      }
    } else {
      // Custom-height: treat as full-height
      if (topShelfOccupied > 0 || bottomShelfOccupied > 0) {
        currentX = currentColumnRightEdge + viewGap;
      }
      
      view.updateGeometry({
        x: currentX,
        y: fullHeightY,
        width,
        height
      });
      
      // Calculate right edge BEFORE updating currentX
      currentColumnRightEdge = currentX + width;
      currentX = currentColumnRightEdge + viewGap;
      
      topShelfOccupied = 2;
      bottomShelfOccupied = 2;
      topShelfRightEdge = currentColumnRightEdge;
      bottomShelfRightEdge = currentColumnRightEdge;
    }
  }
  
  // The resized view stays at its current X position (already updated in toggleSizeState)
  // Just ensure it's positioned correctly with the new dimensions
  const resizedMetadata = resizedView.getMetadata();
  const resizedHeightMode = resizedMetadata?.heightMode ?? 'full';
  const resizedWidthMode = resizedMetadata?.widthMode ?? 'standard';
  const resizedY = resizedHeightMode === 'half' ? verticalPadding : verticalPadding;
  
  resizedView.updateGeometry({
    x: resizedX,
    y: resizedY,
    width: resizedWidth,
    height: resizedGeometry.height || getDefaultViewHeight()
  });
  
  // Position views to the right of the resized view
  // They start right after the resized view's new right edge
  // Continue using the same Tetris-style algorithm
  // Need to determine shelf state after the resized view
  // If resized view is half-height, we need to track its shelf occupancy
  
  if (resizedHeightMode === 'half') {
    // Resized view is half-height - update shelf state
    const resizedUnits = resizedWidthMode === 'double' ? 2 : 1;
    // Determine which shelf the resized view is on based on its Y position
    const resizedYPosition = resizedGeometry.y ?? verticalPadding;
    if (resizedYPosition === fullHeightY) {
      topShelfOccupied = resizedUnits;
    } else {
      bottomShelfOccupied = resizedUnits;
    }
  } else {
    // Resized view is full-height - mark both shelves as FULLY occupied
    // A full-height view occupies the ENTIRE column (both shelves), regardless of its width
    topShelfOccupied = 2;
    bottomShelfOccupied = 2;
  }
  
  currentX = resizedRightEdge + viewGap;
  // Initialize column right edge: if resized view is half-height, track its right edge
  // Otherwise, start fresh after the resized view
  if (resizedHeightMode === 'half') {
    currentColumnRightEdge = Math.max(currentX, resizedRightEdge);
    // Initialize shelf edges based on which shelf the resized view is on
    // We need to check the resized view's Y position to determine which shelf
    const resizedYPosition = resizedGeometry.y ?? verticalPadding;
    const isTopShelf = resizedYPosition === verticalPadding;
    if (isTopShelf) {
      topShelfRightEdge = resizedRightEdge;
      bottomShelfRightEdge = currentX;
    } else {
      bottomShelfRightEdge = resizedRightEdge;
      topShelfRightEdge = currentX;
    }
  } else {
    currentColumnRightEdge = currentX; // Full-height view starts a new column
    topShelfRightEdge = currentX;
    bottomShelfRightEdge = currentX;
  }
  
  for (let i = resizedIndex + 1; i < viewOrder.length; i++) {
    const viewId = viewOrder[i];
    const view = views.get(viewId);
    if (!view) continue;
    
    const geometry = view.getGeometry();
    const width = geometry.width || getDefaultViewWidth();
    const height = geometry.height || getDefaultViewHeight();
    const metadata = view.getMetadata();
    const heightMode = metadata?.heightMode ?? 'full';
    const widthMode = metadata?.widthMode ?? 'standard';
    
    if (heightMode === 'full') {
      // Full-height view: occupies entire column (both shelves)
      if (topShelfOccupied > 0 || bottomShelfOccupied > 0) {
        currentX = currentColumnRightEdge + viewGap;
      }
      
      view.updateGeometry({
        x: currentX,
        y: fullHeightY,
        width,
        height
      });
      
      // Calculate right edge BEFORE updating currentX
      currentColumnRightEdge = currentX + width;
      currentX = currentColumnRightEdge + viewGap;
      
      topShelfOccupied = 2;
      bottomShelfOccupied = 2;
      topShelfRightEdge = currentColumnRightEdge;
      bottomShelfRightEdge = currentColumnRightEdge;
    } else if (heightMode === 'half') {
      // Half-height view: Tetris-style placement
      const unitsNeeded = widthMode === 'double' ? 2 : 1;
      
      if (topShelfOccupied + unitsNeeded <= 2) {
        // Fits on top shelf - add gap if shelf already has views
        const x = topShelfOccupied > 0 ? topShelfRightEdge + viewGap : topShelfRightEdge;
        view.updateGeometry({
          x,
          y: fullHeightY,
          width,
          height
        });
        topShelfOccupied += unitsNeeded;
        topShelfRightEdge = x + width;
        currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
      } else if (bottomShelfOccupied + unitsNeeded <= 2) {
        // Fits on bottom shelf - add gap if shelf already has views
        const x = bottomShelfOccupied > 0 ? bottomShelfRightEdge + viewGap : bottomShelfRightEdge;
        view.updateGeometry({
          x,
          y: bottomShelfY,
          width,
          height
        });
        bottomShelfOccupied += unitsNeeded;
        bottomShelfRightEdge = x + width;
        currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
      } else {
        // Both shelves full - start new column
        currentX = currentColumnRightEdge + viewGap;
        topShelfOccupied = unitsNeeded;
        bottomShelfOccupied = 0;
        topShelfRightEdge = currentX;
        bottomShelfRightEdge = currentX;
        currentColumnRightEdge = currentX;
        view.updateGeometry({
          x: currentX,
          y: fullHeightY,
          width,
          height
        });
        topShelfRightEdge = currentX + width;
        currentColumnRightEdge = topShelfRightEdge;
      }
    } else {
      // Custom-height: treat as full-height
      if (topShelfOccupied > 0 || bottomShelfOccupied > 0) {
        currentX = currentColumnRightEdge + viewGap;
      }
      
      view.updateGeometry({
        x: currentX,
        y: fullHeightY,
        width,
        height
      });
      
      // Calculate right edge BEFORE updating currentX
      currentColumnRightEdge = currentX + width;
      currentX = currentColumnRightEdge + viewGap;
      
      topShelfOccupied = 2;
      bottomShelfOccupied = 2;
      topShelfRightEdge = currentColumnRightEdge;
      bottomShelfRightEdge = currentColumnRightEdge;
    }
  }
  
  // Also position any views not in the order (newly created views)
  // Use the same Tetris-style algorithm
  for (const view of sortedViews) {
    if (!viewOrder.includes(view.id)) {
      const geometry = view.getGeometry();
      const width = geometry.width || getDefaultViewWidth();
      const height = geometry.height || getDefaultViewHeight();
      const metadata = view.getMetadata();
      const heightMode = metadata?.heightMode ?? 'full';
      const widthMode = metadata?.widthMode ?? 'standard';
      
      if (heightMode === 'full') {
        // Full-height view: reset shelves and flow horizontally
        if (topShelfOccupied > 0 || bottomShelfOccupied > 0) {
          currentX = currentColumnRightEdge + viewGap;
        }
        
        view.updateGeometry({
          x: currentX,
          y: fullHeightY,
          width,
          height
        });
        
        currentColumnRightEdge = currentX + width;
        currentX = currentColumnRightEdge + viewGap;
        topShelfOccupied = 2;
        bottomShelfOccupied = 2;
      } else if (heightMode === 'half') {
        // Half-height view: Tetris-style placement
        const unitsNeeded = widthMode === 'double' ? 2 : 1;
        
        if (topShelfOccupied + unitsNeeded <= 2) {
          // Fits on top shelf - add gap if shelf already has views
          const x = topShelfOccupied > 0 ? topShelfRightEdge + viewGap : topShelfRightEdge;
          view.updateGeometry({
            x,
            y: fullHeightY,
            width,
            height
          });
          topShelfOccupied += unitsNeeded;
          topShelfRightEdge = x + width;
          currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
        } else if (bottomShelfOccupied + unitsNeeded <= 2) {
          // Fits on bottom shelf - add gap if shelf already has views
          const x = bottomShelfOccupied > 0 ? bottomShelfRightEdge + viewGap : bottomShelfRightEdge;
          view.updateGeometry({
            x,
            y: bottomShelfY,
            width,
            height
          });
          bottomShelfOccupied += unitsNeeded;
          bottomShelfRightEdge = x + width;
          currentColumnRightEdge = Math.max(currentColumnRightEdge, topShelfRightEdge, bottomShelfRightEdge);
        } else {
          // Both shelves full - start new column
          currentX = currentColumnRightEdge + viewGap;
          topShelfOccupied = unitsNeeded;
          bottomShelfOccupied = 0;
          topShelfRightEdge = currentX;
          bottomShelfRightEdge = currentX;
          currentColumnRightEdge = currentX;
          view.updateGeometry({
            x: currentX,
            y: fullHeightY,
            width,
            height
          });
          topShelfRightEdge = currentX + width;
          currentColumnRightEdge = topShelfRightEdge;
        }
      } else {
        // Custom-height: treat as full-height
        view.updateGeometry({
          x: currentX,
          y: fullHeightY,
          width,
          height
        });
        currentX += width + viewGap;
        topShelfOccupied = 0;
        bottomShelfOccupied = 0;
      }
    }
  }
  
  // Update container width (include all views including resized view)
  const allViewsForWidth = [...sortedViews, resizedView];
  updateContainerWidth(allViewsForWidth);
  
}

/**
 * Update container width to fit all views (enables horizontal scrolling)
 */
function updateContainerWidth(views: BaseViewHTML[]): void {
  // Use cached reference instead of querySelector
  const contentWrapper = cachedLayerContentElement || (document.querySelector('.layer-content') as HTMLElement);
  if (!contentWrapper) return;
  const containerPadding = getContainerPadding();
  
  // Find the rightmost edge of all views
  let maxRight = containerPadding;
  for (const view of views) {
    const geometry = view.getGeometry();
    const right = (geometry.x || 0) + (geometry.width || getDefaultViewWidth());
    maxRight = Math.max(maxRight, right);
  }
  
  // Set container width to accommodate all views + padding
  const minWidth = window.innerWidth; // At least viewport width
  const contentWidth = maxRight + containerPadding;
  contentWrapper.style.width = `${Math.max(minWidth, contentWidth)}px`;
  contentWrapper.style.height = '100%'; // Full height, no vertical scroll
  
  // Visualization lines disabled for now
  // updateVisualizationLines();
}

/**
 * Calculate next position for a new view
 * 
 * Horizontal-first layout:
 * - Full-height views: always at top (y = containerPadding), placed horizontally
 * - Half-height views: can stack vertically at same X position (two per column)
 * - Custom-height views: placed horizontally, can stack if they fit
 */
function calculateNextPosition(
  existingViews: BaseViewHTML[],
  width: number,
  height: number,
  availableWidth: number,
  availableHeight: number,
  heightMode?: 'full' | 'half'
): { x: number; y: number } {
  // Get viewport dimensions
  const horizontalPadding = getContainerPadding();
  const verticalPadding = getVerticalPadding();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxWidth = viewportWidth - (horizontalPadding * 2);
  const maxHeight = viewportHeight - (verticalPadding * 2);
  
  // Build a map of occupied positions with metadata
  const occupiedRects: Array<{ 
    x: number; 
    y: number; 
    width: number; 
    height: number;
    heightMode?: 'full' | 'half';
  }> = [];
  
  for (const view of existingViews) {
    const geometry = view.getGeometry();
    const metadata = view.getMetadata();
    occupiedRects.push({
      x: geometry.x || 0,
      y: geometry.y || 0,
      width: geometry.width || getDefaultViewWidth(),
      height: geometry.height || getDefaultViewHeight(),
      heightMode: metadata?.heightMode
    });
  }
  
  // Sort by x (left to right), then y (top to bottom)
  occupiedRects.sort((a, b) => {
    if (Math.abs(a.x - b.x) < 1) { // Same column (within 1px tolerance)
      return a.y - b.y;
    }
    return a.x - b.x;
  });
  
  // For full-height views: always at top, just find next horizontal position
  if (heightMode === 'full') {
  let currentX = horizontalPadding;
  
    // Find the rightmost edge of all full-height views
  for (const rect of occupiedRects) {
      if (rect.heightMode === 'full' || rect.height >= availableHeight * 0.9) {
        // This is a full-height view, move past it
        currentX = Math.max(currentX, rect.x + rect.width + viewGap);
      }
    }
    
    // Check if we need to move past any half-height views that might be in the way
    for (const rect of occupiedRects) {
      if (rect.x + rect.width > currentX && rect.x < currentX + width) {
        // There's a view overlapping our target position
        currentX = rect.x + rect.width + viewGap;
      }
    }
    
    return { x: currentX, y: verticalPadding };
  }
  
  // For half-height views: try to stack, otherwise place horizontally
  if (heightMode === 'half') {
    const halfHeight = availableHeight / 2;
    const gap = viewGap;
    
    // Try to find a column where we can stack
    for (const rect of occupiedRects) {
      if (rect.heightMode === 'half' || (rect.height <= halfHeight * 1.1 && rect.height >= halfHeight * 0.9)) {
        // This is a half-height view - check if we can stack on top or below
        const stackX = rect.x;
        const stackY = rect.y;
        const stackHeight = rect.height;
        
        // Check if there's space above (if this view is at bottom)
        if (stackY > verticalPadding + halfHeight) {
          // View is positioned below top, check if we can place above it
          const newY = verticalPadding;
          if (newY + halfHeight + gap <= stackY) {
            // Can stack above
            return { x: stackX, y: newY };
          }
        }
        
        // Check if there's space below
        const bottomY = stackY + stackHeight;
        if (bottomY + halfHeight + gap <= verticalPadding + availableHeight) {
          // Can stack below - check if nothing else is there
          let canStackBelow = true;
          for (const otherRect of occupiedRects) {
            if (Math.abs(otherRect.x - stackX) < 1 && 
                otherRect.y >= bottomY && 
                otherRect.y < bottomY + halfHeight + gap) {
              canStackBelow = false;
              break;
            }
          }
          if (canStackBelow) {
            return { x: stackX, y: bottomY + gap };
          }
        }
      }
    }
    
    // Can't stack, place horizontally at top
    let currentX = horizontalPadding;
    for (const rect of occupiedRects) {
      if (rect.x + rect.width > currentX) {
        currentX = rect.x + rect.width + viewGap;
      }
    }
    return { x: currentX, y: verticalPadding };
  }
  
  // For custom-height views: place horizontally, try to stack if small enough
  // Similar to half-height but more flexible
  let currentX = horizontalPadding;
  let currentY = verticalPadding;
  
  // Find rightmost position
  for (const rect of occupiedRects) {
    if (rect.x + rect.width > currentX) {
      currentX = rect.x + rect.width + viewGap;
    }
  }
  
  // For small custom views, try to find stacking opportunity
  if (height <= availableHeight / 2) {
    for (const rect of occupiedRects) {
      // Check if we can stack at this X position
      if (Math.abs(rect.x - currentX) < 1) {
        const bottomY = rect.y + rect.height;
        if (bottomY + height + viewGap <= verticalPadding + availableHeight) {
          // Can stack below
          let canStack = true;
          for (const otherRect of occupiedRects) {
            if (Math.abs(otherRect.x - currentX) < 1 &&
                otherRect.y >= bottomY &&
                otherRect.y < bottomY + height + viewGap) {
              canStack = false;
              break;
            }
          }
          if (canStack) {
            return { x: currentX, y: bottomY + viewGap };
          }
        }
      }
    }
  }
  
  // Default: place at top, horizontally
  return { x: currentX, y: currentY };
}

/**
 * Check if a view instance already exists (by process kind)
 */
export function findExistingView(processId: string, processKind: string): BaseViewHTML | null {
  const views = viewManager.getAllViews();
  
  for (const view of views.values()) {
    if (view.processId === processId) {
      return view;
    }
  }
  
  return null;
}

